type RGBA = { r: number; g: number; b: number; a: number };

const parseColor = (color: string): RGBA | null => {
  const i = parseInt;
  const m = Math.round;
  const len = color.length;

  if (len > 9) {
    const parts = color.split(",");
    if (parts.length < 3 || parts.length > 4) return null;

    const r = i(parts[0].slice(parts[0].indexOf("(") + 1));
    const g = i(parts[1]);
    const b = i(parts[2]);
    const a = parts.length === 4 ? parseFloat(parts[3]) : -1;
    return { r, g, b, a };
  } else {
    if (len !== 4 && len !== 5 && len !== 7 && len !== 9) return null;

    let hex = color.slice(1);
    if (hex.length < 6) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    const val = i(hex, 16);
    if (hex.length === 8) {
      return {
        r: (val >> 24) & 255,
        g: (val >> 16) & 255,
        b: (val >> 8) & 255,
        a: m((val & 255) / 0.255) / 1000,
      };
    } else {
      return {
        r: val >> 16,
        g: (val >> 8) & 255,
        b: val & 255,
        a: -1,
      };
    }
  }
};

export function pSBC(
  p: number,
  c0: string,
  c1?: string,
  linear = false
): string | null {
  const m = Math.round;
  const i = parseInt;

  if (
    typeof p !== "number" ||
    p < -1 ||
    p > 1 ||
    typeof c0 !== "string" ||
    (c0[0] !== "r" && c0[0] !== "#") ||
    (c1 && typeof c1 !== "string")
  )
    return null;

  const from = parseColor(c0);
  const to =
    c1 && c1 !== "c"
      ? parseColor(c1)
      : p < 0
      ? { r: 0, g: 0, b: 0, a: -1 }
      : { r: 255, g: 255, b: 255, a: -1 };

  if (!from || !to) return null;

  const hasAlpha = from.a >= 0 || to.a >= 0;
  const isRGB = c0.startsWith("rgb") || (c1 && c1.startsWith("rgb"));
  const t = p < 0 ? -p : p;
  const P = 1 - t;

  const r = linear
    ? m(P * from.r + t * to.r)
    : m(Math.sqrt(P * from.r ** 2 + t * to.r ** 2));
  const g = linear
    ? m(P * from.g + t * to.g)
    : m(Math.sqrt(P * from.g ** 2 + t * to.g ** 2));
  const b = linear
    ? m(P * from.b + t * to.b)
    : m(Math.sqrt(P * from.b ** 2 + t * to.b ** 2));

  const a = hasAlpha
    ? from.a < 0
      ? to.a
      : to.a < 0
      ? from.a
      : from.a * P + to.a * t
    : 0;

  if (isRGB) {
    return `rgb${hasAlpha ? "a" : ""}(${r},${g},${b}${
      hasAlpha ? `,${Math.round(a * 1000) / 1000}` : ""
    })`;
  } else {
    const hex = (
      0x100000000 +
      (r << 24) +
      (g << 16) +
      (b << 8) +
      (hasAlpha ? m(a * 255) : 0)
    )
      .toString(16)
      .slice(1);
    return `#${hasAlpha ? hex : hex.slice(0, 6)}`;
  }
}
